import { ConfluencePageAri } from '@atlassian/ari/confluence/page';
// eslint-disable-next-line workspaces/require-dependency
import { getConfluencePageAriFromContentRef } from '@databases/connections/utils/ariHelper';
// eslint-disable-next-line workspaces/require-dependency
import { loadContent } from '@databases/connections-api/actions';
import { getCloudId } from '@databases/database-state';
import { logWarning } from '@databases/logs';
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
// eslint-disable-next-line workspaces/require-dependency
import { type GuardedType } from '@databases/types';
import {
  type BlogpostReference,
  type BlogpostReferenceOrContentId,
  type Content,
  type OptionalCreatePageReference,
  type OptionalCreatePageReferenceOrContentId,
} from '@databases/types/confluence';
import {
  DatabaseFieldType,
  type Hyperlink,
  PageSubType,
  type RawDatabaseValue,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { isEqual } from 'lodash-es';

import { getConfluenceService } from '@canvas/atlassian-service/confluence';

import { DATA_LINKED_RESOURCE_ID_ATTR } from '../../confluence/content';
import {
  blogpostToReference,
  contentToReference,
  getContentKey,
  isBlogpostReference,
  isContent,
  isPageReference,
  pageToReference,
  serializeBlogpostReference,
  serializePageReference,
} from '../../confluence/utils';
import { cheerioIterator } from '../../lib/dom';
import { loadContentById } from '../../redux/content/actions';
import { selectContentByIdOptional } from '../../redux/content/selectors';
import { selectBaseUrl } from '../../redux/context/selectors';
import { matchViewPageOrBlogpostPath } from '../../urls';
import { generateId } from '../../utils';
import type { FieldType } from '../field-types';

export async function createPageWithOptions({
  templateId,
  entryUrl,
  spaceKey,
  parentId,
  contentTitle,
  subType,
}: {
  templateId: string | null;
  entryUrl: string | null;
  spaceKey: string;
  parentId: string;
  contentTitle: string;
  subType: PageSubType | null;
}): Promise<Content | undefined> {
  const contentSpecificCreateInput = [];

  entryUrl &&
    contentSpecificCreateInput.push({ key: 'headerContentUrl', value: entryUrl });
  templateId && contentSpecificCreateInput.push({ key: 'templateId', value: templateId });

  try {
    const content = await getConfluenceService().createContent({
      parentId,
      title: contentTitle,
      contentSpecificCreateInput,
      spaceKey,
      type: 'page',
      subType: subType === PageSubType.LIVE ? PageSubType.LIVE : undefined,
    });

    const { links, space, extensions, ...rest } = content;

    return {
      ...rest,
      type: 'page',
      space: space
        ? {
            ...space,
            id: Number(space.id),
          }
        : undefined,
      extensions: extensions
        ? Object.fromEntries(extensions.map(({ key, value }) => [key, value]))
        : undefined,
      _links: links,
    };
  } catch (error: unknown) {
    if (error instanceof Error) {
      logWarning('Error creating page', error);
      throw error;
    }
  }
}

/**
 * Converts a page value to a list of page references. Empty page values that have been added by the user through the UI
 * but are not filled out yet are omitted from the list. Page references by ID are resolved, if they are not in the state,
 * they are omitted.
 */
export function normalizePageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>,
  state: DefaultRootState | undefined
): Array<
  RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
    (OptionalCreatePageReference | BlogpostReference)
> {
  return value.flatMap((v) => {
    const normalized = resolveSinglePageLinkValue(v, state);
    return 'contentTitle' in normalized ? [normalized] : [];
  });
}

export function filterValidPageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>
): Array<
  RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
    (OptionalCreatePageReferenceOrContentId | BlogpostReferenceOrContentId)
> {
  return value.flatMap((v) => {
    if ('contentTitle' in v || 'contentId' in v) {
      return [v];
    }
    return [];
  });
}

function getHyperlinkRepresentation(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>,
  state: DefaultRootState | undefined
): Array<Hyperlink & { _id: string }> {
  const resolvedRefs = value
    .map((v) => {
      if (!v || !state) {
        return undefined;
      }
      if ('create' in v && !v.create) {
        return v;
      }

      if ('contentId' in v) {
        const resolvedRef = selectContentByIdOptional(state, {
          contentId: v.contentId,
        });
        if (resolvedRef) {
          if (
            isContent(resolvedRef) &&
            (resolvedRef.type === 'page' || resolvedRef.type === 'blogpost')
          ) {
            return contentToReference(resolvedRef);
          }
        }
      } else {
        return undefined;
      }
    })
    .filter((v) => v !== undefined && 'url' in v);

  return resolvedRefs.map((v) => ({
    _id: generateId(),
    url: v.url,
    label: v.contentTitle,
  }));
}

/**
 * If the given page value is a page ID reference, attempts to resolve it and return a page reference. If the value is not
 * a page ID reference or the page with the given ID is not in the store, the value is returned unchanged.
 */
export function resolveSinglePageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0],
  state: DefaultRootState | undefined
): RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] {
  if ('contentId' in value) {
    const content =
      state && selectContentByIdOptional(state, { contentId: value.contentId });
    if (content?.type === 'page') {
      return { ...pageToReference(content), _id: value._id };
    } else if (content?.type === 'blogpost') {
      return { ...blogpostToReference(content), _id: value._id };
    }
  }
  return value;
}

/**
 * Returns true if the given page value is not empty. Empty page values are added by the user through the UI
 * but have not been filled with a page reference yet.
 */
function isNonEmptyPageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0]
): value is RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
  (OptionalCreatePageReference | BlogpostReference | { contentId: string }) {
  return 'contentTitle' in value || 'contentId' in value;
}

/**
 * Returns true if the given page link field value represents an existing page or blog post, rather than one to be created or an empty
 * value.
 */
export function isExistingPageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0]
): value is RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
  (OptionalCreatePageReference | BlogpostReference) {
  return (
    ('contentTitle' in value || 'contentId' in value) &&
    (!isPageReference(value) || !value.create)
  );
}

const pageLinkFieldType: FieldType<DatabaseFieldType.PAGE_LINK> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],

  isCsvImportSupported: false,

  renderExportView: ({ value }, state) => {
    return normalizePageLinkValue(value, state)
      .map(
        (v) =>
          `<ac:link ac:card-appearance="inline">${
            isBlogpostReference(v)
              ? serializeBlogpostReference(v)
              : serializePageReference(v)
          }</ac:link>`
      )
      .join('<br />');
  },

  parseFromHtml: (el, context, state) => {
    const baseUrl = selectBaseUrl(state);

    return [...cheerioIterator(el.find('a[href]'))].flatMap((a) => {
      const href = a.attr('href') ?? '';
      if (href.startsWith(baseUrl)) {
        const id = a.attr(DATA_LINKED_RESOURCE_ID_ATTR);
        const match = matchViewPageOrBlogpostPath(href, id);
        if (match) {
          return [
            {
              _id: generateId(),
              contentId: match.contentId,
              ari: ConfluencePageAri.create({
                siteId: getCloudId()!,
                pageId: match.contentId,
              }).toString(),
            },
          ];
        }
      }
      return [];
    });
  },

  getInitialValue: () => [],

  isEmptyValue: ({ value }) => value.filter(isNonEmptyPageLinkValue).length === 0,

  isEqualValue: ({ value: value1 }, { value: value2 }) => {
    const getKey = (v: GuardedType<typeof isNonEmptyPageLinkValue>) =>
      'contentId' in v ? v.contentId : getContentKey(v);
    return isEqual(
      value1.filter(isNonEmptyPageLinkValue).map(getKey),
      value2.filter(isNonEmptyPageLinkValue).map(getKey)
    );
  },

  getStringRepresentation: ({ value }, state) =>
    normalizePageLinkValue(value, state)
      .map((v) => v.contentTitle)
      .join('\n'),

  getReferencedAttachments: () => [],

  getReferencedContent: ({ value }) =>
    filterValidPageLinkValue(value).filter((v) => isExistingPageLinkValue(v)),

  loadDependencies: ({ field, value }, helper) => {
    const dispatch = helper.dispatch;
    for (const v of value) {
      if ('contentId' in v && v.contentId) {
        dispatch(loadContentById(v.contentId));
      }

      const ari = getConfluencePageAriFromContentRef(v);
      if (ari) {
        dispatch(
          loadContent({
            ari,
            connectionId: field.id,
          })
        );
      }
    }
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: ({ value }, state) => ({
      plain: normalizePageLinkValue(value, state)
        .map((v) => v.contentTitle)
        .join('\n'),
    }),
    [DatabaseFieldType.HYPERLINK]: ({ value }, state) =>
      getHyperlinkRepresentation(value, state),
  },
};

export default pageLinkFieldType;
